// How each country writes an address (TF-23), after the address metadata Google publishes for
// libaddressinput: which parts it has, in which order and on which line, what the region and the
// postal code are called, and the postal code's pattern. A country not listed gets the generic form.
//
// In `lines`, %A is the street (line 1 and line 2), %C the city, %S the region, %Z the postal code
// and %n a new line; anything else is written as it stands ("SINGAPORE"). Plain data, no React:
// document generation can use it from a shared library later.

export type RegionKind = 'state' | 'province' | 'county' | 'prefecture' | 'region';
export type PostalKind = 'postal' | 'zip' | 'postcode' | 'pin' | 'eircode';

export interface AddressFormat {
  lines: string;
  region?: RegionKind;
  postal?: PostalKind;
  /** The postal code's pattern, whole and ignoring case. */
  zip?: string;
  zipExample?: string;
  /** Written before a postal code typed without it: LV-1010. */
  zipPrefix?: string;
}

const EU_STYLE = (zip: string, zipExample: string): AddressFormat => ({ lines: '%A%n%Z %C', zip, zipExample });

export const GENERIC_FORMAT: AddressFormat = { lines: '%A%n%Z %C%n%S', region: 'region' };

export const ADDRESS_FORMATS: Record<string, AddressFormat> = {
  // The EU and the EEA.
  AT: EU_STYLE('\\d{4}', '1010'),
  BE: EU_STYLE('\\d{4}', '1000'),
  BG: EU_STYLE('\\d{4}', '1000'),
  CY: EU_STYLE('\\d{4}', '1010'),
  CZ: EU_STYLE('\\d{3} ?\\d{2}', '110 00'),
  DE: EU_STYLE('\\d{5}', '10115'),
  DK: EU_STYLE('\\d{4}', '1050'),
  EE: EU_STYLE('\\d{5}', '10111'),
  ES: { lines: '%A%n%Z %C %S', region: 'province', zip: '\\d{5}', zipExample: '28001' },
  FI: EU_STYLE('\\d{5}', '00100'),
  FR: EU_STYLE('\\d{2} ?\\d{3}', '75001'),
  GR: EU_STYLE('\\d{3} ?\\d{2}', '105 57'),
  HR: EU_STYLE('(HR-)?\\d{5}', '10000'),
  HU: { lines: '%C%n%A%n%Z', zip: '\\d{4}', zipExample: '1011' },
  IE: { lines: '%A%n%C%n%S%n%Z', region: 'county', postal: 'eircode', zip: '[\\dA-Z]{3} ?[\\dA-Z]{4}', zipExample: 'D02 X285' },
  IS: EU_STYLE('\\d{3}', '101'),
  IT: { lines: '%A%n%Z %C %S', region: 'province', zip: '\\d{5}', zipExample: '00144' },
  LI: EU_STYLE('94[89]\\d', '9490'),
  LT: EU_STYLE('(LT-)?\\d{5}', 'LT-01100'),
  LU: EU_STYLE('(L-)?\\d{4}', 'L-1111'),
  LV: { lines: '%A%n%C, %Z', zip: 'LV-\\d{4}', zipExample: 'LV-1010', zipPrefix: 'LV-' },
  MT: { lines: '%A%n%C %Z', zip: '[A-Z]{3} ?\\d{2,4}', zipExample: 'VLT 1117' },
  NL: EU_STYLE('\\d{4} ?[A-Z]{2}', '1012 AB'),
  NO: EU_STYLE('\\d{4}', '0150'),
  PL: EU_STYLE('\\d{2}-\\d{3}', '00-950'),
  PT: EU_STYLE('\\d{4}-\\d{3}', '1000-001'),
  RO: { lines: '%A%n%Z %C%n%S', region: 'county', zip: '\\d{6}', zipExample: '010011' },
  SE: EU_STYLE('\\d{3} ?\\d{2}', '111 22'),
  SI: EU_STYLE('(SI-)?\\d{4}', '1000'),
  SK: EU_STYLE('\\d{3} ?\\d{2}', '811 01'),
  // Elsewhere in Europe.
  CH: EU_STYLE('\\d{4}', '8001'),
  GB: { lines: '%A%n%C%n%Z', postal: 'postcode', zip: 'GIR ?0AA|[A-Z]{1,2}\\d[A-Z\\d]? ?\\d[A-Z]{2}', zipExample: 'SW1A 1AA' },
  // North America, Asia and Australia.
  US: { lines: '%A%n%C, %S %Z', region: 'state', postal: 'zip', zip: '\\d{5}(-\\d{4})?', zipExample: '62701' },
  CA: { lines: '%A%n%C %S %Z', region: 'province', zip: '[A-Z]\\d[A-Z] ?\\d[A-Z]\\d', zipExample: 'K1A 0B1' },
  CN: { lines: '%A%n%C%n%S, %Z', region: 'province', zip: '\\d{6}', zipExample: '100000' },
  JP: { lines: '%A%n%C, %S%n%Z', region: 'prefecture', zip: '\\d{3}-?\\d{4}', zipExample: '100-0001' },
  IN: { lines: '%A%n%C %Z%n%S', region: 'state', postal: 'pin', zip: '\\d{6}', zipExample: '110001' },
  SG: { lines: '%A%nSINGAPORE %Z', zip: '\\d{6}', zipExample: '018956' },
  AU: { lines: '%A%n%C %S %Z', region: 'state', zip: '\\d{4}', zipExample: '2000' },
};

export const formatOf = (country: string | null | undefined): AddressFormat => (country && ADDRESS_FORMATS[country]) || GENERIC_FORMAT;

/** Every ISO 3166-1 country code, for the country list (named by the browser in the app language). */
export const COUNTRY_CODES = `AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW
  BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE
  GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW
  KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO
  NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX
  SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW`.split(/\s+/);
